import { useQuery } from '@tanstack/react-query';
import { getShippings, getShipping, getCanadaPostTracking, getPCShipmentStatuses, getAllAccountNames } from '../api';
import { shippingKeys } from './queryKeys';
import type { ShippingsFilters } from '../types';

/**
 * Hook to fetch all account names for searchable dropdown filters
 */
export function useAllAccounts() {
  return useQuery({
    queryKey: [...shippingKeys.all, 'accounts'],
    queryFn: getAllAccountNames,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch shippings list with filters
 */
export function useShippings(filters: ShippingsFilters = {}) {
  return useQuery({
    queryKey: shippingKeys.list(filters),
    queryFn: () => getShippings(filters),
  });
}

/**
 * Hook to fetch a single shipping by ID
 */
export function useShipping(id: string) {
  return useQuery({
    queryKey: shippingKeys.detail(id),
    queryFn: () => getShipping(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch Canada Post tracking data
 * Batches requests for multiple tracking numbers
 */
export function useCanadaPostTracking(trackingNumbers: string[], enabled = true) {
  const validNumbers = trackingNumbers.filter(Boolean);

  return useQuery({
    queryKey: shippingKeys.canadaPostTracking(validNumbers),
    queryFn: () => getCanadaPostTracking(validNumbers),
    enabled: enabled && validNumbers.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch Planète Courrier delivery statuses
 */
export function usePCTracking(shipmentNumbers: string[], enabled = true) {
  const validNumbers = shipmentNumbers.filter(Boolean);

  return useQuery({
    queryKey: [...shippingKeys.tracking(), 'planete-courrier', validNumbers],
    queryFn: () => getPCShipmentStatuses(validNumbers),
    enabled: enabled && validNumbers.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
